import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  formatDate,
  formatPhone,
  MEMBER_ROLE_LABEL,
  MEMBER_ROLES,
  type MemberRole,
  type OrganizationDetail,
  type OrganizationRow,
  ORG_STATUS_LABEL,
  ORG_STATUSES,
  ORG_TYPE_LABEL,
  ORG_TYPES,
  type OrgStatus,
  type OrgType,
  telHref,
  yandexNavUrl,
} from '@reikraft/shared';
import {
  type Column,
  Dot,
  ErrorBox,
  Field,
  Icon,
  Kpi,
  Kpis,
  Loader,
  Meter,
  Modal,
  Notice,
  Panel,
  SearchBox,
  Table,
  useToast,
} from '@reikraft/ui';
import { api, errorText, qs } from '../api';
import { FormMessage, Hint, OrderCards, PageHead, useFormErrors } from '../components/common';
import { PaymentModal } from '../components/PaymentModal';
import { formatMoney, kopToRub, rubToKop } from '../format';
import { useDebounced } from '../hooks';
import { useFlag } from '../session';

const STATUS_COLOR: Record<OrgStatus, string> = { lead: 'var(--accent)', active: 'var(--success)', blocked: 'var(--danger)' };

interface OrgForm {
  type: OrgType;
  name: string;
  inn: string;
  kpp: string;
  address: string;
  contactName: string;
  contactPhone: string;
  email: string;
  contractNo: string;
  paymentTermsDays: string;
  creditLimit: string;
  status: OrgStatus;
  notes: string;
}

const toForm = (g?: OrganizationRow): OrgForm => ({
  type: g?.type ?? 'service',
  name: g?.name ?? '',
  inn: g?.inn ?? '',
  kpp: g?.kpp ?? '',
  address: g?.address ?? '',
  contactName: g?.contactName ?? '',
  contactPhone: g?.contactPhone ?? '',
  email: g?.email ?? '',
  contractNo: g?.contractNo ?? '',
  paymentTermsDays: String(g?.paymentTermsDays ?? 0),
  creditLimit: kopToRub(g?.creditLimitKop),
  status: g?.status ?? 'active',
  notes: g?.notes ?? '',
});

function OrgModal({ open, onClose, org }: { open: boolean; onClose: () => void; org?: OrganizationRow }) {
  const [f, setF] = useState<OrgForm>(toForm(org));
  const errs = useFormErrors();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  useEffect(() => {
    if (open) {
      setF(toForm(org));
      errs.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const save = useMutation({
    mutationFn: () => {
      const body = { ...f, paymentTermsDays: Number(f.paymentTermsDays) || 0, creditLimitKop: rubToKop(f.creditLimit) };
      return org
        ? api.put<OrganizationDetail>(`/admin/organizations/${org.id}`, body)
        : api.post<OrganizationDetail>('/admin/organizations', body);
    },
    onSuccess: (g) => {
      void qc.invalidateQueries({ queryKey: ['organizations'] });
      qc.setQueryData(['organization', g.id], g);
      toast('Организация сохранена');
      onClose();
      if (!org) void navigate(`/organizations/${g.id}`);
    },
    onError: (e) => errs.set(e),
  });
  const bind = (k: keyof OrgForm) => ({ value: f[k], onChange: (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value }) });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      wide
      title={org ? 'Организация' : 'Новая организация'}
      footer={
        <button type="submit" form="org-form" className="rk-btn rk-btn--primary" disabled={save.isPending}>
          Сохранить
        </button>
      }
    >
      <form id="org-form" onSubmit={submit} className="rk-stack">
        <FormMessage message={errs.message} />
        <div className="rk-grid-form">
          <Field label="Название" error={errs.errors.name}>
            <input className="rk-input" {...bind('name')} autoFocus placeholder="СТО «Привод»" />
          </Field>
          <Field label="Тип">
            <select className="rk-select" {...bind('type')}>
              {ORG_TYPES.map((t) => (
                <option key={t} value={t}>
                  {ORG_TYPE_LABEL[t]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Статус">
            <select className="rk-select" {...bind('status')}>
              {ORG_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {ORG_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Контактное лицо">
            <input className="rk-input" {...bind('contactName')} />
          </Field>
          <Field label="Телефон" error={errs.errors.contactPhone}>
            <input className="rk-input rk-input--mono" type="tel" {...bind('contactPhone')} />
          </Field>
          <Field label="E-mail" error={errs.errors.email}>
            <input className="rk-input" type="email" {...bind('email')} />
          </Field>
          <Field label="ИНН" error={errs.errors.inn}>
            <input className="rk-input rk-input--mono" inputMode="numeric" {...bind('inn')} />
          </Field>
          <Field label="КПП">
            <input className="rk-input rk-input--mono" inputMode="numeric" {...bind('kpp')} />
          </Field>
          <Field label="№ договора">
            <input className="rk-input rk-input--mono" {...bind('contractNo')} />
          </Field>
          <Field label="Отсрочка, дней">
            <input className="rk-input rk-input--mono" type="number" min={0} max={365} {...bind('paymentTermsDays')} />
          </Field>
          <Field label="Лимит долга, ₽" hint="Пусто — без лимита">
            <input className="rk-input rk-input--mono" inputMode="numeric" {...bind('creditLimit')} />
          </Field>
        </div>
        <Field label="Адрес">
          <input className="rk-input" {...bind('address')} />
        </Field>
        <Field label="Заметки">
          <textarea className="rk-textarea" rows={2} {...bind('notes')} />
        </Field>
      </form>
    </Modal>
  );
}

export function OrganizationsPage() {
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const list = useQuery({
    queryKey: ['organizations', dq],
    queryFn: () => api.get<OrganizationRow[]>(`/admin/organizations${qs({ q: dq })}`),
  });
  const columns: Column<OrganizationRow>[] = [
    {
      key: 'name',
      title: 'Организация',
      width: 'minmax(200px, 1.4fr)',
      render: (g) => (
        <span className="rk-td--stack">
          <b style={{ fontWeight: 600 }}>{g.name}</b>
          <span className="rk-td__sub">
            {ORG_TYPE_LABEL[g.type]}
            {g.inn ? ` · ИНН ${g.inn}` : ''}
          </span>
        </span>
      ),
    },
    {
      key: 'contact',
      title: 'Контакт',
      width: 'minmax(160px, 1fr)',
      render: (g) => (
        <span className="rk-td--stack">
          <span>{g.contactName ?? '—'}</span>
          <span className="rk-td__mono-sub">{formatPhone(g.contactPhone)}</span>
        </span>
      ),
    },
    { key: 'active', title: 'В работе', width: '90px', render: (g) => <span className="rk-mono">{g.activeOrders}</span> },
    { key: 'orders', title: 'Всего', width: '70px', render: (g) => <span className="rk-mono rk-muted">{g.ordersCount}</span> },
    {
      key: 'debt',
      title: 'Долг',
      width: '120px',
      align: 'right',
      render: (g) => (
        <b
          className="rk-mono"
          style={{ color: g.creditLimitKop !== null && g.debtKop > g.creditLimitKop ? 'var(--danger-text)' : undefined }}
        >
          {g.debtKop ? formatMoney(g.debtKop) : '—'}
        </b>
      ),
    },
    {
      key: 'status',
      title: 'Статус',
      width: '130px',
      render: (g) => (
        <span className="rk-badge">
          <Dot color={STATUS_COLOR[g.status]} size="sm" />
          {ORG_STATUS_LABEL[g.status].toUpperCase()}
        </span>
      ),
    },
  ];
  return (
    <>
      <PageHead
        title="Организации"
        sub="Автосервисы, автопарки и таксопарки"
        actions={
          <button type="button" className="rk-btn rk-btn--primary" onClick={() => setOpen(true)}>
            <Icon name="plus" size={16} /> Новая организация
          </button>
        }
      >
        <SearchBox value={q} onChange={setQ} placeholder="Название, ИНН, контакт" label="Поиск организации" />
      </PageHead>
      {list.isLoading ? (
        <Loader />
      ) : list.isError ? (
        <ErrorBox error={list.error} onRetry={() => list.refetch()} />
      ) : (
        <Table
          label="Организации"
          columns={columns}
          rows={list.data!}
          rowKey={(g) => g.id}
          onRowClick={(g) => navigate(`/organizations/${g.id}`)}
          minWidth={820}
          empty="Организаций нет"
        />
      )}
      <OrgModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}

export function OrganizationPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const b2b = useFlag('b2b_cabinet');
  const documents = useFlag('documents');
  const [edit, setEdit] = useState(false);
  const [pay, setPay] = useState(false);
  const [member, setMember] = useState(false);
  const [invoice, setInvoice] = useState(false);
  const key = ['organization', id];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<OrganizationDetail>(`/admin/organizations/${id}`) });
  const mutate = useMutation({
    mutationFn: ({ method, path, body }: { method: 'post' | 'patch' | 'del'; path: string; body?: unknown }) =>
      method === 'del' ? api.del(path) : api[method](path, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
    onError: (e) => toast(errorText(e), { error: true }),
  });
  const [site, setSite] = useState({ title: '', address: '', point: '' });
  const [veh, setVeh] = useState({ plate: '', car: '' });

  if (q.isLoading) return <Loader />;
  if (q.isError || !q.data) return <ErrorBox error={q.error} />;
  const g = q.data;
  const resetPassword = (userId: string) => {
    const pw = window.prompt('Новый пароль (минимум 8 символов)');
    if (pw)
      mutate.mutate(
        { method: 'patch', path: `/admin/organizations/${id}/members/${userId}`, body: { password: pw } },
        { onSuccess: () => toast('Пароль изменён') },
      );
  };

  return (
    <>
      <PageHead
        crumbs={[{ label: 'Организации', to: '/organizations' }, { label: g.name }]}
        title={g.name}
        sub={[
          ORG_TYPE_LABEL[g.type],
          g.inn ? `ИНН ${g.inn}` : null,
          g.contractNo ? `договор № ${g.contractNo}` : null,
          g.paymentTermsDays ? `отсрочка ${g.paymentTermsDays} дн.` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <>
            {g.contactPhone && (
              <a className="rk-btn" href={telHref(g.contactPhone)}>
                Позвонить
              </a>
            )}
            <button type="button" className="rk-btn" onClick={() => setEdit(true)}>
              Реквизиты
            </button>
            {documents && (
              <button type="button" className="rk-btn" onClick={() => setInvoice(true)}>
                Выставить счёт
              </button>
            )}
            <Link to={`/orders/new?organizationId=${g.id}&name=${encodeURIComponent(g.name)}`} className="rk-btn rk-btn--primary">
              Новый заказ
            </Link>
          </>
        }
      />
      {g.status === 'lead' && (
        <Notice
          tone="accent"
          label="Новая организация"
          actions={
            <button type="button" className="rk-btn rk-btn--sm" onClick={() => setEdit(true)}>
              Проверить и активировать
            </button>
          }
        >
          Заявка на подключение — проверьте реквизиты и договоритесь об условиях.
        </Notice>
      )}
      <Kpis>
        <Kpi label="В работе" value={g.activeOrders} />
        <Kpi label="Заказов всего" value={g.ordersCount} />
        <Kpi
          label={g.creditLimitKop ? `Долг · лимит ${formatMoney(g.creditLimitKop)}` : 'Долг'}
          value={formatMoney(g.debtKop)}
          tone={g.creditLimitKop !== null && g.debtKop > g.creditLimitKop ? 'danger' : undefined}
        >
          {g.creditLimitKop ? (
            <Meter
              value={g.debtKop}
              max={g.creditLimitKop}
              tone={g.debtKop > g.creditLimitKop ? 'danger' : g.debtKop > g.creditLimitKop * 0.8 ? 'warn' : undefined}
            />
          ) : null}
          {g.debtKop > 0 && (
            <button type="button" className="rk-btn rk-btn--sm" style={{ marginTop: 8 }} onClick={() => setPay(true)}>
              Принять оплату
            </button>
          )}
        </Kpi>
      </Kpis>
      <div className="adm-cols">
        <div className="adm-cols__main">
          <Panel title="Заказы" bodyless>
            <OrderCards items={g.orders} />
          </Panel>
        </div>
        <div className="adm-cols__side">
          <Panel
            title={`Доступ в кабинет · ${g.members.length}`}
            actions={
              <button type="button" className="rk-btn rk-btn--sm" onClick={() => setMember(true)}>
                + Сотрудник
              </button>
            }
            bodyless
          >
            {!b2b && (
              <div className="rk-panel__body">
                <Hint>Кабинет для организаций выключен в настройках. Логины заработают после включения.</Hint>
              </div>
            )}
            <div className="rk-rows">
              {g.members.map((m) => (
                <div key={m.userId} className="rk-stack" style={{ padding: '10px 18px', ['--rk-gap' as string]: '4px' }}>
                  <span className="rk-row" style={{ justifyContent: 'space-between' }}>
                    <b style={{ opacity: m.isActive ? 1 : 0.5 }}>{m.name}</b>
                    <span className="rk-muted" style={{ fontSize: 12 }}>
                      {MEMBER_ROLE_LABEL[m.role]}
                    </span>
                  </span>
                  <span className="rk-muted" style={{ fontSize: 12 }}>
                    {m.email} · {m.lastLoginAt ? `входил ${formatDate(m.lastLoginAt)}` : 'ещё не входил'}
                  </span>
                  <span className="rk-row" style={{ ['--rk-gap' as string]: '12px' }}>
                    <button type="button" className="rk-btn rk-btn--link rk-btn--sm" onClick={() => resetPassword(m.userId)}>
                      Сменить пароль
                    </button>
                    <button
                      type="button"
                      className="rk-btn rk-btn--link rk-btn--sm"
                      onClick={() =>
                        mutate.mutate({
                          method: 'patch',
                          path: `/admin/organizations/${id}/members/${m.userId}`,
                          body: { isActive: !m.isActive },
                        })
                      }
                    >
                      {m.isActive ? 'Отключить' : 'Включить'}
                    </button>
                  </span>
                </div>
              ))}
            </div>
          </Panel>
          <Panel title="Площадки для забора" bodyless>
            <div className="rk-rows">
              {g.sites.map((s) => (
                <div key={s.id} className="rk-row" style={{ padding: '10px 18px', justifyContent: 'space-between', flexWrap: 'nowrap' }}>
                  <span className="rk-td--stack">
                    <b>{s.title}</b>
                    <span className="rk-muted" style={{ fontSize: 12 }}>
                      {s.address}
                    </span>
                    <a
                      href={yandexNavUrl({ geoPoint: s.geoPoint, mapUrl: s.mapUrl, address: s.address }) ?? undefined}
                      target="_blank"
                      rel="noreferrer"
                      style={{ fontSize: 12 }}
                    >
                      {s.geoPoint || s.mapUrl ? 'Точка на карте ↗' : 'Найти на карте ↗'}
                    </a>
                  </span>
                  <button
                    type="button"
                    className="rk-btn rk-btn--sm rk-btn--icon"
                    aria-label={`Удалить площадку ${s.title}`}
                    onClick={() => mutate.mutate({ method: 'del', path: `/admin/organizations/${id}/sites/${s.id}` })}
                  >
                    ×
                  </button>
                </div>
              ))}
              <form
                className="rk-stack"
                style={{ padding: '10px 18px', ['--rk-gap' as string]: '8px' }}
                onSubmit={(e) => {
                  e.preventDefault();
                  mutate.mutate(
                    { method: 'post', path: `/admin/organizations/${id}/sites`, body: { ...site, point: site.point || null } },
                    { onSuccess: () => setSite({ title: '', address: '', point: '' }) },
                  );
                }}
              >
                <input
                  className="rk-input"
                  placeholder="Название, например «Парковка № 1»"
                  value={site.title}
                  onChange={(e) => setSite({ ...site, title: e.target.value })}
                />
                <input
                  className="rk-input"
                  placeholder="Адрес"
                  value={site.address}
                  onChange={(e) => setSite({ ...site, address: e.target.value })}
                />
                <input
                  className="rk-input"
                  placeholder="Точка на Яндекс Картах: ссылка «Поделиться» или координаты"
                  aria-label="Точка на Яндекс Картах"
                  value={site.point}
                  onChange={(e) => setSite({ ...site, point: e.target.value })}
                />
                <button type="submit" className="rk-btn rk-btn--sm" disabled={!site.title || !site.address}>
                  Добавить площадку
                </button>
              </form>
            </div>
          </Panel>
          <Panel title={`Автомобили · ${g.vehicles.length}`} bodyless>
            <div className="rk-rows">
              {g.vehicles.map((v) => (
                <div key={v.id} className="rk-row" style={{ padding: '10px 18px', justifyContent: 'space-between', flexWrap: 'nowrap' }}>
                  <span className="rk-td--stack">
                    <b className="rk-mono">{v.plate}</b>
                    <span className="rk-muted" style={{ fontSize: 12 }}>
                      {v.car ?? '—'} · заказов {v.ordersCount}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="rk-btn rk-btn--sm rk-btn--icon"
                    aria-label={`Удалить ${v.plate}`}
                    onClick={() => mutate.mutate({ method: 'del', path: `/admin/organizations/${id}/vehicles/${v.id}` })}
                  >
                    ×
                  </button>
                </div>
              ))}
              <form
                className="rk-row"
                style={{ padding: '10px 18px', ['--rk-gap' as string]: '8px' }}
                onSubmit={(e) => {
                  e.preventDefault();
                  mutate.mutate(
                    { method: 'post', path: `/admin/organizations/${id}/vehicles`, body: { plate: veh.plate, car: veh.car || null } },
                    { onSuccess: () => setVeh({ plate: '', car: '' }) },
                  );
                }}
              >
                <input
                  className="rk-input rk-input--mono"
                  style={{ flex: '1 1 110px' }}
                  placeholder="Госномер"
                  value={veh.plate}
                  onChange={(e) => setVeh({ ...veh, plate: e.target.value })}
                />
                <input
                  className="rk-input"
                  style={{ flex: '2 1 140px' }}
                  placeholder="Автомобиль"
                  value={veh.car}
                  onChange={(e) => setVeh({ ...veh, car: e.target.value })}
                />
                <button type="submit" className="rk-btn rk-btn--sm" disabled={!veh.plate}>
                  +
                </button>
              </form>
            </div>
          </Panel>
          {g.notes && <Panel title="Заметки">{g.notes}</Panel>}
        </div>
      </div>
      <OrgModal open={edit} onClose={() => setEdit(false)} org={g} />
      <MemberModal open={member} onClose={() => setMember(false)} orgId={g.id} />
      <PaymentModal
        open={pay}
        onClose={() => setPay(false)}
        party={{ kind: 'organization', id: g.id, name: g.name }}
        defaultAmountKop={g.debtKop}
      />
      {documents && <InvoiceModal open={invoice} onClose={() => setInvoice(false)} org={g} />}
    </>
  );
}

function MemberModal({ open, onClose, orgId }: { open: boolean; onClose: () => void; orgId: string }) {
  const [f, setF] = useState({ name: '', email: '', phone: '', role: 'manager' as MemberRole, position: '', password: '' });
  const errs = useFormErrors();
  const qc = useQueryClient();
  const toast = useToast();
  useEffect(() => {
    if (open) {
      setF({ name: '', email: '', phone: '', role: 'manager', position: '', password: '' });
      errs.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const save = useMutation({
    mutationFn: () => api.post(`/admin/organizations/${orgId}/members`, f),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['organization', orgId] });
      toast('Доступ создан — передайте логин и пароль сотруднику');
      onClose();
    },
    onError: (e) => errs.set(e),
  });
  const bind = (k: keyof typeof f) => ({
    value: f[k],
    onChange: (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value }),
  });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Доступ в кабинет"
      footer={
        <button type="button" className="rk-btn rk-btn--primary" disabled={save.isPending} onClick={() => save.mutate()}>
          Создать доступ
        </button>
      }
    >
      <FormMessage message={errs.message} />
      <div className="rk-grid-form">
        <Field label="Имя" error={errs.errors.name}>
          <input className="rk-input" {...bind('name')} />
        </Field>
        <Field label="Должность">
          <input className="rk-input" {...bind('position')} placeholder="механик парка" />
        </Field>
        <Field label="E-mail (логин)" error={errs.errors.email}>
          <input className="rk-input" type="email" {...bind('email')} />
        </Field>
        <Field label="Телефон" error={errs.errors.phone}>
          <input className="rk-input rk-input--mono" type="tel" {...bind('phone')} />
        </Field>
        <Field label="Роль">
          <select className="rk-select" {...bind('role')}>
            {MEMBER_ROLES.map((r) => (
              <option key={r} value={r}>
                {MEMBER_ROLE_LABEL[r]}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Пароль" error={errs.errors.password} hint="Минимум 8 символов">
          <input className="rk-input rk-input--mono" type="text" autoComplete="new-password" {...bind('password')} />
        </Field>
      </div>
    </Modal>
  );
}

function InvoiceModal({ open, onClose, org }: { open: boolean; onClose: () => void; org: OrganizationDetail }) {
  const qc = useQueryClient();
  const toast = useToast();
  const unpaid = useQuery({
    queryKey: ['unpaid', 'organization', org.id],
    queryFn: () => api.get<{ id: string; number: string; totalKop: number; paidKop: number }[]>(`/admin/unpaid?orgId=${org.id}`),
    enabled: open,
  });
  const [ids, setIds] = useState<string[]>([]);
  const create = useMutation({
    mutationFn: () => api.post<{ url: string; number: string }>('/admin/documents/invoice', { organizationId: org.id, orderIds: ids }),
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
      toast(`Счёт ${d.number} выставлен`);
      window.open(d.url, '_blank', 'noopener');
      onClose();
    },
    onError: (e) => toast(errorText(e), { error: true }),
  });
  const total = (unpaid.data ?? []).filter((u) => ids.includes(u.id)).reduce((s, u) => s + u.totalKop - u.paidKop, 0);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Счёт на оплату"
      footer={
        <button type="button" className="rk-btn rk-btn--primary" disabled={!ids.length || create.isPending} onClick={() => create.mutate()}>
          Выставить · {formatMoney(total)}
        </button>
      }
    >
      {(unpaid.data ?? []).map((u) => (
        <label key={u.id} className="rk-check">
          <input
            type="checkbox"
            checked={ids.includes(u.id)}
            onChange={(e) => setIds(e.target.checked ? [...ids, u.id] : ids.filter((x) => x !== u.id))}
          />
          <span className="rk-mono">{u.number}</span> · к оплате {formatMoney(u.totalKop - u.paidKop)}
        </label>
      ))}
      {unpaid.data && !unpaid.data.length && <Hint>Неоплаченных заказов нет.</Hint>}
    </Modal>
  );
}
